// src/components/collections/CollectionDetailDrawer.tsx
//
// Opens when a user taps a collection tile on the Collections page.
//
// Layout brief from the user:
//   - Hero image on the LEFT (large)
//   - On the right: 2-row vertical grid of CIRCLE tiles for each
//     item. Found items get a green ring + checkmark badge; unfound
//     ones get a neutral border. Truncate to a "+N more" tile if
//     the collection has too many items to fit.
//   - Tap a circle → reveal a SECONDARY detail card (slide-up or
//     overlay) with the item's full title, description, reveal_hint,
//     and — when coordinates are available — a "Walking directions"
//     button that opens a NavigateToCard sheet inline.
//
// We use Drawer + a nested second Drawer for the item-detail. Nested
// drawers are fine in MUI; both share the maxHeight pattern with
// the rest of the app (HighlightDetailSheet, NavigateToCard).
//
// Why bottom-anchor: matches every other detail sheet in the app
// (CityHighlights, NavigateToCard, ContextSettings). One convention,
// less for the user to learn.

import React, { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Chip,
  Skeleton,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  AutoAwesome,
  Place,
  Directions,
  MoreHoriz,
} from '@mui/icons-material';
import { CollectionProgress } from '../../types/collections';
import { useCollectionDetail, CollectionDetailItem } from '../../hooks/useCollectionDetail';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';
import { useT } from '../../i18n/useT';
import { NavigateToCard } from '../navigate/NavigateToCard';
import type { NavigateTarget } from '../../utils/interactiveContent';

interface Props {
  progress: CollectionProgress | null;
  onClose: () => void;
}

// Sizing for the circle tiles. Slightly smaller than CityHighlights'
// TILE_SIZE=76 because we're packing two rows of them next to a
// hero image inside a sheet.
const CIRCLE_SIZE = 64;
// Cap visible circles before showing a "+N more" tile. With CIRCLE_SIZE=64
// and gap=10, 6 tiles fit in two rows of three; collections with more
// items get a "+N more" 7th tile.
const MAX_VISIBLE_CIRCLES = 6;

const CircleTile: React.FC<{
  item: CollectionDetailItem;
  onClick: () => void;
}> = ({ item, onClick }) => {
  const t = useT();
  // Prefer the user's own photo when they've found the item; fall
  // back to the canonical Wikipedia/AI cover. The found photo path
  // is a Storage path that needs the resolver.
  const imageSource = item.found_at
    ? item.found_photo_reference || item.cover_image_thumb || item.cover_image_url
    : item.cover_image_thumb || item.cover_image_url;
  const cover = useResolvedImageUrl(imageSource || undefined);
  const found = !!item.found_at;

  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label={t('Open {title}', { title: item.title })}
      sx={{
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        transition: 'transform 120ms ease-out',
        '&:active': { transform: 'scale(0.94)' },
      }}
    >
      <Box
        sx={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          overflow: 'hidden',
          position: 'relative',
          backgroundImage: cover ? `url(${cover})` : undefined,
          backgroundColor: cover ? undefined : 'grey.300',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          // Green ring when found — matches the convention from
          // CityHighlightsRow ("Picked for you"). Subtle fade on
          // found tiles so the user's eye gravitates to what's
          // still missing.
          border: found ? '3px solid' : '2px solid',
          borderColor: found ? 'success.main' : 'divider',
          opacity: found ? 0.85 : 1,
        }}
      >
        {!cover && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Place fontSize="small" />
          </Box>
        )}
        {found && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'success.main',
              border: '2px solid',
              borderColor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle sx={{ fontSize: 12, color: 'white' }} />
          </Box>
        )}
      </Box>
      <Typography
        variant="caption"
        align="center"
        sx={{
          width: CIRCLE_SIZE + 8,
          lineHeight: 1.15,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: '0.68rem',
          fontWeight: found ? 400 : 500,
          color: found ? 'text.secondary' : 'text.primary',
        }}
      >
        {item.title}
      </Typography>
    </Box>
  );
};

const MoreTile: React.FC<{ count: number; onClick: () => void }> = ({
  count,
  onClick,
}) => {
  const t = useT();
  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label={t('Show {count} more', { count })}
      sx={{
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          border: '2px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <MoreHoriz sx={{ fontSize: 18 }} />
          <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.65rem' }}>
            +{count}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" align="center" sx={{ fontSize: '0.68rem' }}>
        {t('more')}
      </Typography>
    </Box>
  );
};

// Inner item-detail sheet — opens on top of the parent drawer.
// Tappable "Walking directions" button surfaces NavigateToCard
// in controlled mode when coords are available.
const ItemDetailSheet: React.FC<{
  item: CollectionDetailItem | null;
  onClose: () => void;
}> = ({ item, onClose }) => {
  const t = useT();
  // Resolve a hero image for the item-detail sheet. Prefer the
  // found photo when present (it's more personal), else fall back
  // to the canonical cover.
  const heroSource = item?.found_at
    ? item.found_photo_reference || item.cover_image_url || item.cover_image_thumb
    : item?.cover_image_url || item?.cover_image_thumb;
  const hero = useResolvedImageUrl(heroSource || undefined);
  const [navigateOpen, setNavigateOpen] = useState(false);

  if (!item) return null;
  const found = !!item.found_at;
  const hasCoords =
    typeof item.lat === 'number' &&
    typeof item.lng === 'number' &&
    !(item.lat === 0 && item.lng === 0);

  const navigateTarget: NavigateTarget | null = hasCoords
    ? {
        name: item.title,
        lat: item.lat as number,
        lng: item.lng as number,
        reasoning: item.description || item.reveal_hint || '',
        ...(item.place_id ? { place_id: item.place_id } : {}),
      }
    : null;

  return (
    <Drawer
      anchor="bottom"
      open={!!item}
      onClose={onClose}
      // Sit on top of the parent collection drawer. MUI defaults
      // already stack correctly; explicit zIndex isn't needed.
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85dvh',
          pb: 'env(safe-area-inset-bottom, 0px)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {hero && (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundImage: `url(${hero})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: 'grey.300',
            }}
          />
        )}
        <IconButton
          onClick={onClose}
          aria-label={t('Close')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Close />
        </IconButton>
        {found && (
          <Chip
            size="small"
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label={t('Found')}
            color="success"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'white' },
              color: 'white',
            }}
          />
        )}
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {item.title}
        </Typography>

        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {item.description}
          </Typography>
        )}

        {item.reveal_hint && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <AutoAwesome
              sx={{
                fontSize: 18,
                color: 'primary.main',
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {item.reveal_hint}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {navigateTarget && (
            <Button
              variant="contained"
              startIcon={<Directions />}
              fullWidth
              onClick={() => setNavigateOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {t('Walking directions')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Walking-directions sheet (NavigateToCard) in controlled
          mode. Renders nothing inline (hideTeaser=true) and pops
          its own bottom sheet when `navigateOpen` is true. */}
      {navigateTarget && (
        <NavigateToCard
          target={navigateTarget}
          open={navigateOpen}
          onClose={() => setNavigateOpen(false)}
          hideTeaser
        />
      )}
    </Drawer>
  );
};

export const CollectionDetailDrawer: React.FC<Props> = ({
  progress,
  onClose,
}) => {
  const t = useT();
  const { detail, items, isLoading } = useCollectionDetail(
    progress?.collection_id || null,
    progress?.collection_ref_path || null
  );
  const [selectedItem, setSelectedItem] =
    useState<CollectionDetailItem | null>(null);
  const [showAllItems, setShowAllItems] = useState(false);

  // Hero comes from the root collection doc when fetched, falling
  // back to the progress record's mirror. Resolve through the URL
  // resolver in case it's a Storage path.
  const heroSource =
    detail?.cover_image_url || progress?.collection_cover_image_url || null;
  const hero = useResolvedImageUrl(heroSource || undefined);

  // Sort: undiscovered first (still to-find), then found (already
  // checked). Matches the "what's left to do" affordance from
  // CityHighlightsRow.
  const sortedItems = useMemo(() => {
    if (!items) return [];
    const unfound = items.filter((i) => !i.found_at);
    const found = items.filter((i) => !!i.found_at);
    return [...unfound, ...found];
  }, [items]);

  const visibleItems = showAllItems
    ? sortedItems
    : sortedItems.slice(0, MAX_VISIBLE_CIRCLES);
  const hiddenCount = Math.max(
    0,
    sortedItems.length - MAX_VISIBLE_CIRCLES
  );
  const hasMore = !showAllItems && hiddenCount > 0;

  const isCompleted = !!progress?.completed_at;
  const isHidden = progress?.hidden_total;
  const pct = !progress
    ? 0
    : isHidden
      ? 100
      : progress.total_count > 0
        ? Math.min(
            100,
            Math.round((progress.found_count / progress.total_count) * 100)
          )
        : 0;

  return (
    <>
      <Drawer
        anchor="bottom"
        open={!!progress}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '85dvh',
            pb: 'env(safe-area-inset-bottom, 0px)',
          },
        }}
      >
        {/* Drag-handle pill — matches the app's other bottom sheets */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
            pb: 0.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              bgcolor: 'action.disabled',
            }}
          />
        </Box>

        {/* Close in top-right of the body */}
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            aria-label={t('Close')}
            sx={{
              position: 'absolute',
              top: 4,
              right: 8,
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, pb: 2.5 }}>
          {/* Top region: hero left, circle grid right. Stacks on
              narrow screens so a small phone doesn't squeeze the
              hero down to a stamp. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            {/* Hero image */}
            <Box
              sx={{
                width: { xs: '100%', sm: '40%' },
                aspectRatio: { xs: '16 / 9', sm: '4 / 5' },
                flexShrink: 0,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundImage: hero ? `url(${hero})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: hero ? undefined : 'grey.200',
                position: 'relative',
              }}
            >
              {!hero && (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <AutoAwesome fontSize="large" />
                </Box>
              )}
              {isCompleted && (
                <Chip
                  size="small"
                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                  label={t('Complete')}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'success.main',
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' },
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* Right side: title, tagline, progress, then the
                two-row circle grid */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
              >
                {progress?.collection_title || detail?.title}
              </Typography>
              {(detail?.tagline || progress?.collection_tagline) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    lineHeight: 1.35,
                  }}
                >
                  {detail?.tagline || progress?.collection_tagline}
                </Typography>
              )}

              {/* Progress strip */}
              {progress && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {isHidden
                        ? t('{count} found', {
                            count: progress.found_count,
                          })
                        : t('{found} of {total}', {
                            found: progress.found_count,
                            total: progress.total_count,
                          })}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {pct}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={isCompleted ? 'success' : 'primary'}
                    sx={{
                      height: 5,
                      borderRadius: 999,
                      bgcolor: 'action.hover',
                    }}
                  />
                </Box>
              )}

              {/* Circle grid — 2 rows. Items wrap into two lines
                  with `flexWrap: wrap` + height cap. */}
              {isLoading ? (
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="circular"
                      width={CIRCLE_SIZE}
                      height={CIRCLE_SIZE}
                    />
                  ))}
                </Box>
              ) : sortedItems.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.25,
                    rowGap: 1.5,
                  }}
                >
                  {visibleItems.map((item) => (
                    <CircleTile
                      key={item.id}
                      item={item}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                  {hasMore && (
                    <MoreTile
                      count={hiddenCount}
                      onClick={() => setShowAllItems(true)}
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {progress?.hidden_total
                    ? t('More items will be revealed as you find them.')
                    : t('Items will appear here.')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Long description, below the hero+grid block. Optional —
              not every collection has one. */}
          {detail?.description && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                {detail.description}
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Item-detail sheet — opens on tap of a circle */}
      <ItemDetailSheet
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </>
  );
};

export default CollectionDetailDrawer;
